import { Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import { ICardinal } from "./ICardinal";
import { ICardinalLoadStrategy } from "./ICardinalLoadStrategy";
import { CardinalV2Loader } from "./CardinalV2Loader";
import { CardinalV1Loader } from "./CardinalV1Loader";
import { CardinalCspMonitor } from "./CardinalCspMonitor";
import { type IInternalsMonitor } from "../../../application/core/services/monitoring/IInternalsMonitor";

export class CardinalV2WithFallbackLoader implements ICardinalLoadStrategy {
  constructor(
    private v2Loader: CardinalV2Loader,
    private v1Loader: CardinalV1Loader,
    private cspMonitor: CardinalCspMonitor,
    private internalMonitor: IInternalsMonitor,
    private songbirdDomains: string[],
    private isLive: boolean,
  ) {}

  load(): Observable<ICardinal> {
    return new Observable<ICardinal>((subscriber) => {
      let hasDetectedCspViolation = false;

      // Monitor for CSP violations
      const cspSubscription = this.cspMonitor
        .monitor(this.songbirdDomains)
        .subscribe(() => {
          hasDetectedCspViolation = true;
        });

      // Attempt V2 load
      const loadSubscription = this.v2Loader
        .load()
        .pipe(
          catchError((error) => {
            // Classify and record error
            const errorType = this.classifyError(
              error,
              hasDetectedCspViolation,
            );

            let errorMessage;

            if (errorType === "CSP_VIOLATION") {
              errorMessage =
                "Cardinal V2 URL is blocked by CSP policy. Falling back to V1";
            } else {
              errorMessage =
                error.message ||
                "Unknown error while loading Cardinal V2 script";
            }

            const contextError = new Error(errorMessage);
            (contextError as any).errorType = errorType;
            (contextError as any).originalError = error;

            if (!this.isLive) {
              this.internalMonitor.recordIssue(contextError, {
                function: "cardinalV2Load",
                errorType,
                originalErrorName: error.name,
                originalErrorMessage: error.message,
              });
            }

            // Fallback to V1
            return this.v1Loader.load();
          }),
        )
        .subscribe({
          next: (cardinal) => {
            cspSubscription.unsubscribe();
            subscriber.next(cardinal);
            subscriber.complete();
          },
          error: (err) => {
            cspSubscription.unsubscribe();
            subscriber.error(err);
          },
        });

      return () => {
        cspSubscription.unsubscribe();
        loadSubscription.unsubscribe();
      };
    });
  }

  private classifyError(error: any, cspDetected: boolean): string {
    if (cspDetected || error.errorType === "CSP_VIOLATION") {
      return "CSP_VIOLATION";
    }
    if (error.errorType) {
      return error.errorType;
    }
    if (error.name === "TypeError" && error.message?.includes("fetch")) {
      return "NETWORK_ERROR";
    }
    if (error.message?.includes("timeout")) {
      return "TIMEOUT";
    }
    if (error.message?.includes("integrity")) {
      return "SRI_FAILURE";
    }
    return "UNKNOWN";
  }
}
